"use client";

import React, {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import Image from "next/image";
import clsx from "clsx";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import {
  ACCORDION_PANEL_BG,
  AccordionChevron,
  accordionBodyClosed,
  accordionBodyOpen,
  accordionCard,
  accordionDivider,
  accordionHeader,
  accordionPanel,
  accordionPanelClip,
  accordionPanelClosed,
  accordionPanelOpen,
} from "../common/accordion";
import NetworkTrafficFlow from "./NetworkTrafficFlow";
import TailorMadeSelector from "./TailorMadeSelector";
import ConnectivityArt from "./ConnectivityArt";
import RealWorldArt from "./RealWorldArt";
import SecurityReplicationArt from "./SecurityReplicationArt";
import VirtualizedWorkstationFlow from "./VirtualizedWorkstationFlow";
import AuthenticPLCArt from "./AuthenticPLCArt";
// DefaultPanelsArt (the traced control-cabinet illustration) used to be the
// default panel; it was replaced by the walkthrough video below. The component
// is left in the tree so it can be dropped back in if the video is pulled.

interface KeyFeature {
  id: string;
  /** Button label shown in the left list. */
  title: string;
  /** Bitmap illustration shown in the right panel. Omitted when `art` is set. */
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  /** Supporting copy shown below the illustration. */
  description: string;
  /** Animated vector layer drawn on top of the illustration, if any. */
  overlay?: React.ReactNode;
  /**
   * Vector rebuild of the illustration, animated. Replaces `image` where the
   * Figma export was a flat bitmap that had to be re-traced to move at all.
   */
  art?: React.ReactNode;
  /**
   * Kept in the list but not offered to the visitor. The entry stays here (with
   * its artwork wired up) rather than being deleted so it can be switched back
   * on by dropping this one line.
   */
  hidden?: boolean;
}

/**
 * "Optional Attack Simulation" is a MITRE-style radar whose story is a sequence:
 * flow lines leave the wheel, reach the technique chips, and the selected chip
 * expands into its sub-techniques. The Figma export is one flattened bitmap
 * (even its .svg is just a base64 PNG), so `scripts/extract-attack-layers.mjs`
 * splits the artwork into a static base plus an overlay holding every animated
 * part in place, and re-measures the dotted ring as real vector.
 *
 * The overlay is drawn three times, each `clip-path`ed to one band of the same
 * file, so the three phases can be timed independently off a single request:
 *   x    0..806   flow lines + their sockets   swept left to right
 *   x  807..1099  the 14 technique chips       swept top to bottom
 *   x 1100..1600  leader + sub-technique panel swept top to bottom
 * Percentages below are those pixel bounds over the 1600x777 artwork box.
 */
const OVERLAY_SRC = "/images/OTSecurity/cyber-range/optional-attack-overlay.png";

/**
 * Dotted ring geometry. A circle fit over the artwork's own 4x4px dots gives
 * centre (363.49, 378.93) r 244.198, with 180 dots at 2deg steps starting at
 * 3 o'clock and running clockwise.
 */
const RING = { cx: 363.49, cy: 378.93, r: 244.2, dots: 180, dotR: 2.1 };
/** Artwork palette: the red of the dots, and the grey-white of its node markers. */
const RING_RED = "#F41E3D";
const RING_WHITE = "#CCCCCC";
/**
 * Which dots run white, as [first dot index, how many]. Index 0 is 3 o'clock and
 * each step is 2deg, so 45 is 6 o'clock, 90 is 9 o'clock, 135 is 12 o'clock. A
 * few arcs plus scattered singles — enough to make the 45deg sweep legible
 * without the ring reading as half white.
 */
const RING_WHITE_RUNS: readonly [number, number][] = [
  [18, 7], [33, 1], [46, 4], [59, 1], [86, 8], [101, 1],
  [116, 2], [131, 1], [154, 8], [169, 1], [175, 1],
];

const RING_DOTS = Array.from({ length: RING.dots }, (_, i) => {
  const rad = (i * (360 / RING.dots) * Math.PI) / 180;
  return {
    cx: +(RING.cx + RING.r * Math.cos(rad)).toFixed(2),
    cy: +(RING.cy + RING.r * Math.sin(rad)).toFixed(2),
    fill: RING_WHITE_RUNS.some(([start, len]) => i >= start && i < start + len)
      ? RING_WHITE
      : RING_RED,
  };
});

/**
 * The two tick combs around SYSTEM, lifted onto their own layers so they can
 * turn. They pivot on the ring centre, given as a percentage of the artwork box
 * (363.49/1600, 378.93/777) so it holds at whatever size the card renders.
 */
const DIALS = [
  { name: "inner", src: "/images/OTSecurity/cyber-range/optional-attack-dial-inner.png" },
  { name: "outer", src: "/images/OTSecurity/cyber-range/optional-attack-dial-outer.png" },
] as const;

const AttackFlow = () => (
  <>
    {DIALS.map((dial) => (
      <Image
        key={dial.name}
        src={dial.src}
        alt=""
        aria-hidden="true"
        width={1600}
        height={777}
        sizes="(max-width: 1024px) 90vw, 560px"
        className={`ot-attack-dial-${dial.name} pointer-events-none absolute inset-0 h-full w-full`}
      />
    ))}
    <svg
      viewBox="0 0 1600 777"
      fill="none"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    >
      {/* The dot set is symmetric about the ring centre, so the group's fill-box
          centre is the ring centre and `transform-origin: center` rotates true. */}
      <g className="ot-attack-ring">
        {RING_DOTS.map((dot, i) => (
          <circle key={i} cx={dot.cx} cy={dot.cy} r={RING.dotR} fill={dot.fill} />
        ))}
      </g>
    </svg>
    {(["flow", "chips", "panel"] as const).map((phase) => (
      <Image
        key={phase}
        src={OVERLAY_SRC}
        alt=""
        aria-hidden="true"
        width={1600}
        height={777}
        sizes="(max-width: 1024px) 90vw, 560px"
        className={`ot-attack-${phase} pointer-events-none absolute inset-0 h-full w-full`}
      />
    ))}
  </>
);

/**
 * "Realistic Configuration Transfer" tells a four-beat story that the flat
 * artwork already lays out left to right: the CLIENT node is read, the data
 * crosses to the hub, the hub processes it, and the SCADA/OT mirror is built.
 *
 * The artwork is a bitmap like the radar above, but it is a strict 7-colour
 * flat design whose circles and lines fit to better than 0.1px, so instead of
 * cutting bitmap layers the moving parts are RE-DRAWN here as vector on the
 * measured geometry. `scripts/extract-config-transfer-layers.mjs` rubs exactly
 * those parts off the base image so nothing is painted twice; everything it
 * leaves alone is animated additively (sweeps, travelling dashes, glows) and so
 * cannot drift out of register.
 *
 * All coordinates are in the artwork's own 1600x576 space.
 */
const CFG = {
  /** CLIENT node. */
  left: { cx: 369, cy: 308.2, r: 159.3 },
  /** The 3 dots inside the client disc, read left to right. */
  discDots: [331.6, 369.2, 406.8],
  /** Hub rings. Note each was fitted separately — the centres really do differ. */
  ring: { cx: 800.65, cy: 173.71, r: 142.74 },
  gauge: { d: "M 796.97 296.79 A 122.48 122.48 0 0 1 801.86 51.88", w: 11.73 },
  red: { d: "M 909.27 61.11 A 160.27 160.27 0 0 1 907.26 288.68", w: 4.76 },
  iris: { cx: 795.5, cy: 174.3, r: 46.95, arc: "M 843.74 174.30 A 48.24 48.24 0 0 1 795.50 222.54" },
  /** The two leader lines, each traced off the bitmap. Data runs P0 -> P2. */
  leadA: "M 536.1 145.9 L 599.9 145.9 L 639.6 106.1",
  leadB: "M 969.8 106.1 L 1009.5 145.9 L 1073.3 145.9",
  /** SCADA/OT mirror — the half that builds itself. */
  node: { cx: 1238.4, cy: 308.2, r: 159.27, coreR: 9.54, fillR: 8.56 },
  spokes: [
    [1245.9, 315.5, 1312.7, 380.7],
    [1231.1, 315.7, 1166.0, 382.6],
    [1230.9, 300.9, 1164.1, 235.8],
    [1245.7, 300.7, 1310.9, 233.9],
  ],
  innerDots: [
    [1266.1, 335.4],
    [1212.1, 335.4],
    [1212.1, 282.3],
    [1263.6, 282.3],
  ],
  outerDots: [
    [1312.7, 380.7],
    [1166.0, 382.6],
    [1164.1, 235.8],
    [1310.9, 233.9],
  ],
  nodeTicks: [
    [1339.7, 405.6, 1353.8, 419.4],
    [1140.7, 408.2, 1126.5, 422.9],
    [1138.2, 209.3, 1124.3, 195.7],
    [1337.1, 206.7, 1350.5, 192.9],
  ],
  cornerDots: [
    [1391.6, 155.0],
    [1085.2, 155.0],
    [1085.2, 461.4],
    [1391.6, 461.4],
  ],
  /**
   * The four circle-and-slash icons on the SCADA half. The artwork ghosts them
   * in #3c4451/#585a66 while the client half's icons are full white, so they are
   * rubbed off the base and redrawn white — they read as instruments coming
   * online once the mirror exists. Bottom-right is the same icon at 1.325x,
   * clipped by the artwork edge exactly as the bitmap had it.
   */
  slashIcons: [
    {
      cx: 982.8, cy: 404.7, r: 39.31, sw: 1.95,
      icy: 405.0, ir: 12.7, isw: 0.77,
      slash: [973.5, 395.8, 992.0, 414.3], ssw: 0.7,
      hy: 405.0, hRuns: [[942.6, 970.5], [995.1, 1023.0]], hsw: 0.68,
      dots: [[1021.3, 443.2], [944.3, 443.2], [944.3, 366.2], [1021.3, 366.2]], dr: 1.82,
    },
    {
      cx: 982.8, cy: 522.9, r: 39.31, sw: 1.95,
      icy: 523.2, ir: 12.7, isw: 0.77,
      slash: [973.5, 514.0, 992.0, 532.6], ssw: 0.78,
      hy: 523.2, hRuns: [[942.6, 970.5], [995.1, 1023.0]], hsw: 0.67,
      dots: [[1021.3, 561.4], [944.3, 561.4], [944.3, 484.4], [1021.3, 484.4]], dr: 1.81,
    },
    {
      cx: 1495.7, cy: 404.7, r: 39.31, sw: 2.03,
      icy: 405.0, ir: 12.7, isw: 0.78,
      slash: [1486.3, 395.8, 1504.8, 414.4], ssw: 0.73,
      hy: 405.0, hRuns: [[1455.4, 1483.3], [1507.9, 1535.9]], hsw: 0.68,
      dots: [[1534.1, 443.2], [1457.2, 443.2], [1457.2, 366.2], [1534.1, 366.2]], dr: 1.86,
    },
    {
      cx: 1496.4, cy: 522.9, r: 52.09, sw: 1.96,
      icy: 522.9, ir: 16.73, isw: 0.77,
      slash: [1484.1, 510.9, 1508.3, 535.2], ssw: 0.72,
      hy: 522.9, hRuns: [[1443.4, 1480.0], [1512.7, 1549.5]], hsw: 0.83,
      dots: [], dr: 1.85,
    },
  ],
  /** The client half's icons, already white in the artwork — halo targets. */
  litIcons: [
    { cx: 112.4, cy: 404.7, r: 39.3 },
    { cx: 112.5, cy: 522.9, r: 39.3 },
    { cx: 628.8, cy: 404.7, r: 39.3 },
    { cx: 628.9, cy: 522.9, r: 52.1 },
  ],
  /**
   * The client icons turn too, but each one only has one part that rotation can
   * be seen on. The target's rings are perfectly concentric, so its diameter rule
   * is what sweeps; the triangle icon carries the same circle-and-slash motif as
   * the SCADA side, so its slash is the needle. The Windows mark is left alone —
   * spinning a brand logo would read as a bug.
   */
  target: {
    cx: 112.5, cy: 522.9,
    ring: 39.3, ringSw: 1.99,
    ringB: 16.6, ringA: 12.7, ringSw2: 0.98,
    core: 3.3, coreSw: 0.5,
    rule: [[71.9, 100.4], [124.6, 153.1]], ruleSw: 0.67,
  },
  triangleCore: {
    cx: 628.8, cy: 404.7, r: 12.7, sw: 0.8,
    slash: [620.0, 413.4, 637.6, 395.9], slashSw: 0.8,
  },
} as const;

/** The folded-square glyph, cut out of the artwork so it can be spun. */
const CUBE_SRC = "/images/OTSecurity/cyber-range/realistic-config-cube.png";

const WHITE = "#ebece6";
const RED = "#ec1e3c";
const GREY = "#9b9a9a";
const SLATE = "#3c4451";

const ConfigTransferFlow = () => (
  <svg
    viewBox="0 0 1600 576"
    fill="none"
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 h-full w-full"
  >
    {/* The cube glyph rides inside the overlay as an <image> so it shares the
        artwork's coordinate system — its pivot is just the icon centre. */}
    <image
      className="cfg-cube"
      href={CUBE_SRC}
      x={0}
      y={0}
      width={1600}
      height={576}
      style={{ transformOrigin: "112.4px 404.7px" }}
    />
    <defs>
      <filter id="cfgGlow" x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="4" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      {/* The gauge fills by growing a plain arc through this mask, so the lit
          ticks keep the original 2deg pitch instead of stretching. */}
      <mask id="cfgGaugeMask" maskUnits="userSpaceOnUse">
        <path
          className="cfg-gauge-mask"
          d={CFG.gauge.d}
          stroke="#fff"
          strokeWidth={CFG.gauge.w + 1}
          pathLength={100}
          strokeDasharray="100 100"
        />
      </mask>
    </defs>

    {/* ---- Phase 1: the client system is read ------------------------- */}
    <circle
      className="cfg-scan-left"
      cx={CFG.left.cx}
      cy={CFG.left.cy}
      r={CFG.left.r}
      stroke={WHITE}
      strokeWidth={2.6}
      strokeLinecap="round"
      pathLength={100}
      strokeDasharray="9 91"
      filter="url(#cfgGlow)"
    />
    {CFG.discDots.map((cx, i) => (
      <circle
        key={cx}
        className={`cfg-read-dot cfg-read-dot-${i}`}
        cx={cx}
        cy={CFG.left.cy}
        r={3.5}
        fill={RED}
        filter="url(#cfgGlow)"
      />
    ))}

    {/* ---- Phase 2 + 4: packets crossing the leader lines -------------- */}
    <path
      className="cfg-flow-a"
      d={CFG.leadA}
      stroke={RED}
      strokeWidth={2.4}
      strokeLinecap="round"
      pathLength={100}
      strokeDasharray="11 89"
      filter="url(#cfgGlow)"
    />
    <path
      className="cfg-flow-b"
      d={CFG.leadB}
      stroke={RED}
      strokeWidth={2.4}
      strokeLinecap="round"
      pathLength={100}
      strokeDasharray="11 89"
      filter="url(#cfgGlow)"
    />

    {/* ---- Phase 3: the hub processes --------------------------------- */}
    {/* Lit copy of the tick gauge, revealed bottom -> top through the mask. */}
    <g className="cfg-gauge-lit" mask="url(#cfgGaugeMask)">
      <path
        d={CFG.gauge.d}
        stroke={WHITE}
        strokeWidth={CFG.gauge.w}
        strokeDasharray="2.867 1.407"
      />
    </g>
    {/* A bright dash orbiting the dashed ring: the hub is always listening. */}
    <circle
      className="cfg-ring-sweep"
      cx={CFG.ring.cx}
      cy={CFG.ring.cy}
      r={CFG.ring.r}
      stroke={WHITE}
      strokeWidth={1.6}
      pathLength={100}
      strokeDasharray="6 94"
      opacity={0.55}
    />
    {/* Iris: the thin ring is static, the 90deg band spins (it is the only
        asymmetric element in the core, so it is the one rotation can be seen on). */}
    <circle cx={CFG.iris.cx} cy={CFG.iris.cy} r={CFG.iris.r} stroke={GREY} strokeWidth={0.46} />
    <path className="cfg-iris" d={CFG.iris.arc} stroke={GREY} strokeWidth={2.09} />
    {/* The red arc is the "100%" beat — drawn on, then left breathing. */}
    <path
      className="cfg-red-arc"
      d={CFG.red.d}
      stroke={RED}
      strokeWidth={CFG.red.w}
      pathLength={100}
      strokeDasharray="100 100"
    />

    {/* ---- Phase 4: the mirror builds itself -------------------------- */}
    <g className="cfg-node-core">
      <circle cx={CFG.node.cx} cy={CFG.node.cy} r={CFG.node.fillR} fill={SLATE} />
      <circle cx={CFG.node.cx} cy={CFG.node.cy} r={CFG.node.coreR} stroke={WHITE} strokeWidth={1.95} />
    </g>
    {CFG.spokes.map(([x1, y1, x2, y2], i) => (
      <line
        key={`sp-${i}`}
        className={`cfg-spoke cfg-spoke-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={WHITE}
        strokeWidth={0.98}
        pathLength={100}
        strokeDasharray="100 100"
      />
    ))}
    {CFG.innerDots.map(([cx, cy], i) => (
      <circle
        key={`id-${i}`}
        className={`cfg-pop cfg-pop-inner-${i}`}
        cx={cx}
        cy={cy}
        r={2.38}
        fill={SLATE}
        stroke={WHITE}
        strokeWidth={0.56}
        style={{ transformOrigin: `${cx}px ${cy}px` }}
      />
    ))}
    {CFG.outerDots.map(([cx, cy], i) => (
      <circle
        key={`od-${i}`}
        className={`cfg-pop cfg-pop-outer-${i}`}
        cx={cx}
        cy={cy}
        r={2.73}
        fill={WHITE}
        style={{ transformOrigin: `${cx}px ${cy}px` }}
      />
    ))}
    <circle
      className="cfg-node-ring"
      cx={CFG.node.cx}
      cy={CFG.node.cy}
      r={CFG.node.r}
      stroke={WHITE}
      strokeWidth={1.94}
      pathLength={100}
      strokeDasharray="100 100"
      transform={`rotate(-90 ${CFG.node.cx} ${CFG.node.cy})`}
    />
    {CFG.nodeTicks.map(([x1, y1, x2, y2], i) => (
      <line
        key={`nt-${i}`}
        className="cfg-node-tick"
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={WHITE}
        strokeWidth={0.98}
      />
    ))}
    {CFG.cornerDots.map(([cx, cy], i) => (
      <circle
        key={`cd-${i}`}
        className={`cfg-pop cfg-pop-corner-${i}`}
        cx={cx}
        cy={cy}
        r={7.05}
        fill={WHITE}
        style={{ transformOrigin: `${cx}px ${cy}px` }}
      />
    ))}
    {/* ---- Phase 5: the instrument row comes online -------------------- */}
    {CFG.slashIcons.map((ic, i) => (
      <g
        key={`ic-${i}`}
        className={`cfg-icon cfg-icon-${i}`}
        style={{ transformOrigin: `${ic.cx}px ${ic.cy}px` }}
      >
        {ic.hRuns.map(([x0, x1], j) => (
          <line key={j} x1={x0} y1={ic.hy} x2={x1} y2={ic.hy} stroke={WHITE} strokeWidth={ic.hsw} />
        ))}
        <circle cx={ic.cx} cy={ic.cy} r={ic.r} stroke={WHITE} strokeWidth={ic.sw} />
        <circle cx={ic.cx} cy={ic.icy} r={ic.ir} stroke={WHITE} strokeWidth={ic.isw} />
        {/* Only the slash turns: the icon's ring and rule stay put, and a 45deg
            chord reads as a needle sweeping the dial. */}
        <line
          className="cfg-icon-slash"
          x1={ic.slash[0]}
          y1={ic.slash[1]}
          x2={ic.slash[2]}
          y2={ic.slash[3]}
          stroke={WHITE}
          strokeWidth={ic.ssw}
          style={{ transformOrigin: `${ic.cx}px ${ic.icy}px` }}
        />
        {ic.dots.map(([dx, dy], j) => (
          <circle key={`d-${j}`} cx={dx} cy={dy} r={ic.dr} fill={WHITE} />
        ))}
      </g>
    ))}
    {/* The client half's two turning instruments, redrawn because their faces had
        to come off the base for the moving part to be separable. */}
    <g>
      <circle cx={CFG.target.cx} cy={CFG.target.cy} r={CFG.target.ring} stroke={WHITE} strokeWidth={CFG.target.ringSw} />
      <circle cx={CFG.target.cx} cy={CFG.target.cy} r={CFG.target.ringB} stroke={WHITE} strokeWidth={CFG.target.ringSw2} />
      <circle cx={CFG.target.cx} cy={CFG.target.cy} r={CFG.target.ringA} stroke={WHITE} strokeWidth={CFG.target.ringSw2} />
      <circle cx={CFG.target.cx} cy={CFG.target.cy} r={CFG.target.core} stroke={WHITE} strokeWidth={CFG.target.coreSw} />
      <g className="cfg-target-rule" style={{ transformOrigin: `${CFG.target.cx}px ${CFG.target.cy}px` }}>
        {CFG.target.rule.map(([x0, x1]) => (
          <line
            key={x0}
            x1={x0}
            y1={CFG.target.cy}
            x2={x1}
            y2={CFG.target.cy}
            stroke={WHITE}
            strokeWidth={CFG.target.ruleSw}
          />
        ))}
      </g>
    </g>
    <g>
      <circle
        cx={CFG.triangleCore.cx}
        cy={CFG.triangleCore.cy}
        r={CFG.triangleCore.r}
        stroke={WHITE}
        strokeWidth={CFG.triangleCore.sw}
      />
      <line
        className="cfg-tri-slash"
        x1={CFG.triangleCore.slash[0]}
        y1={CFG.triangleCore.slash[1]}
        x2={CFG.triangleCore.slash[2]}
        y2={CFG.triangleCore.slash[3]}
        stroke={WHITE}
        strokeWidth={CFG.triangleCore.slashSw}
        style={{ transformOrigin: `${CFG.triangleCore.cx}px ${CFG.triangleCore.cy}px` }}
      />
    </g>

    {/* A halo pings along the whole instrument row, left to right. */}
    {[...CFG.litIcons, ...CFG.slashIcons].map((ic, i) => (
      <circle
        key={`ping-${i}`}
        className={`cfg-ping cfg-ping-${i}`}
        cx={ic.cx}
        cy={ic.cy}
        r={ic.r}
        stroke={WHITE}
        strokeWidth={1.6}
        style={{ transformOrigin: `${ic.cx}px ${ic.cy}px` }}
      />
    ))}

    {/* Once built, the mirror keeps the same radar sweep as the client node. */}
    <circle
      className="cfg-scan-right"
      cx={CFG.node.cx}
      cy={CFG.node.cy}
      r={CFG.node.r}
      stroke={WHITE}
      strokeWidth={2.6}
      strokeLinecap="round"
      pathLength={100}
      strokeDasharray="9 91"
      filter="url(#cfgGlow)"
    />
  </svg>
);

/**
 * KEY FEATURES — matches the Figma "OT" component set (node 109:59153).
 * Selecting a feature on the left swaps the illustration + description in the
 * right card. Mirrors each variant (Type=Default, Type=Connectivity, ...).
 */
const FEATURES: KeyFeature[] = [
  {
    id: "connectivity",
    title: "Connectivity With Level 0 Devices",
    art: <ConnectivityArt />,
    description:
      "The simulation system links to Level 0 devices, including sensors, meters, and pumps, all interacting with a miniature replica of your plant. This unique feature provides an immersive and highly authentic learning experience.",
  },
  {
    id: "virtualized-workstation",
    title: "Virtualized Workstation Experience",
    hidden: true,
    image: "/images/OTSecurity/cyber-range/virtualized-workstation.png",
    imageWidth: 1600,
    imageHeight: 344,
    description:
      "Our platform offers the virtualization of crucial workstations such as the Human Machine Interface (HMI), engineering stations, historians, and Database allowing for in-depth system understanding and exploration.",
    overlay: <VirtualizedWorkstationFlow />,
  },
  {
    id: "tailor-made",
    title: "Tailor-made For Your Needs",
    image: "/images/OTSecurity/cyber-range/tailor-made-base.png",
    imageWidth: 1600,
    imageHeight: 701,
    description:
      "Our OT/ICS Simulation System is customized for your unique requirements. From staff training to system modification testing, from cybersecurity threat preparation to efficiency optimization, our platform provides a risk-free and real-world environment for achieving your objectives.",
    overlay: <TailorMadeSelector />,
  },
  {
    id: "realistic-config",
    title: "Realistic Configuration Transfer",
    image: "/images/OTSecurity/cyber-range/realistic-config-base.png",
    imageWidth: 1600,
    imageHeight: 576,
    description:
      "Our platform can import all your system's configurations, security settings, and patch levels, ensuring a high-fidelity mirror of your live environment.",
    overlay: <ConfigTransferFlow />,
  },
  {
    id: "security-replication",
    title: "Security Replication",
    hidden: true,
    art: <SecurityReplicationArt />,
    description:
      "Our platform can optionally replicate your existing security infrastructure, including firewalls, intrusion detection systems, security information and event management systems, and endpoint protection. This enables thorough security system testing and enhancement.",
  },
  {
    id: "network-traffic",
    title: "Network Traffic Simulation",
    hidden: true,
    image: "/images/OTSecurity/cyber-range/network-traffic-base.png",
    imageWidth: 1600,
    imageHeight: 577,
    description:
      "OT/ICS Simulation system can mimic typical network traffic within the platform, presenting a complete and accurate representation of daily operations.",
    overlay: <NetworkTrafficFlow />,
  },
  {
    id: "optional-attack",
    title: "Optional Attack Simulation",
    image: "/images/OTSecurity/cyber-range/optional-attack-base.png",
    imageWidth: 1600,
    imageHeight: 777,
    description:
      "Clients can opt for additional attack simulations or other security products to rigorously test their system's defenses and incident responses.",
    overlay: <AttackFlow />,
  },
  {
    id: "real-world",
    title: "Real-world Process Simulation",
    art: <RealWorldArt />,
    description:
      "Our platform perfectly models the processes of your actual operational environments, from water plants to power generation and distribution facilities, providing a comprehensive and realistic training ground.",
  },
  {
    id: "authentic-plc",
    title: "Authentic PLC Integration",
    art: <AuthenticPLCArt />,
    description:
      "At the heart of our simulation lies the integration of real programmable logic controllers (PLCs), paving the way for practical training and testing scenarios which closely mimic real-world operations.",
  },
];

/** What the visitor actually sees — `hidden` entries stay in FEATURES but out of here. */
const VISIBLE_FEATURES = FEATURES.filter((f) => !f.hidden);

/** Slide 0 is the default video panel, so there is one more slide than features. */
const SLIDE_COUNT = VISIBLE_FEATURES.length + 1;

// The default panel's walkthrough. Re-encoded from the 1328x930 source
// ("Thùng máy 3D scada.mp4") to 1280x896 H.264 — 2.9MB instead of 6.5MB, still
// ~2x the 642px the panel gives it. The poster is its own first frame.
const VIDEO_SRC = "/images/OTSecurity/cyber-range/scada-cabinet-3d.mp4";
const VIDEO_POSTER =
  "/images/OTSecurity/cyber-range/scada-cabinet-3d-poster.jpg";

/**
 * The shared accordion's own panel duration (`accordionPanel` carries
 * `duration-[380ms]`). It is module-private over there, so this is a copy —
 * change one and change the other. Used only to time when a closing row's
 * illustration may leave the DOM.
 */
const PANEL_MS = 380;

/**
 * Illustration + copy for one feature. Shared by the `lg`+ slide and the
 * sub-`lg` card below the list, which are two renderings of the same content
 * (only one is ever in the DOM — the other side is `display:none`).
 */
function FeatureBody({ feature }: { feature: KeyFeature }) {
  return (
    <>
      {/* The illustration fills the card's width. `w-full h-auto` matters for
          more than size: it makes the box drive layout instead of whatever
          variant next/image happens to serve (with `w-auto` the art rendered
          at the `sizes` hint, 560px inside a 629px card), and it lets the
          width/height attributes reserve the height up front so the card no
          longer collapses while a newly selected illustration loads.
          The wrapper tracks that box exactly, so an overlay pinned to
          inset-0 lands on the artwork. */}
      <div className="relative w-full">
        {feature.art ? (
          <div className="w-full">{feature.art}</div>
        ) : (
          <Image
            src={feature.image!}
            alt={feature.title}
            width={feature.imageWidth}
            height={feature.imageHeight}
            sizes="(max-width: 1024px) 90vw, 640px"
            className="h-auto w-full max-h-full object-contain"
          />
        )}
        {feature.overlay}
      </div>
      <p className="text-text-highlight text-[15px] sm:text-[16px] leading-[28px] sm:leading-[30px] tracking-[0.3px] text-justify mt-6">
        {feature.description}
      </p>
    </>
  );
}

const OTKeyFeatures = () => {
  // 0 = the default video panel; 1..n = the features. One index drives the tab
  // list, the slide track and the dots, the same way the HackGrid product page
  // does it (see HackgridProduct.tsx) — Figma's Type=Default is slide 0, i.e.
  // no feature is selected until the visitor picks one.
  const [active, setActive] = useState(0);
  const go = (i: number) => setActive((i + SLIDE_COUNT) % SLIDE_COUNT);
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isDesktop = useIsDesktop();
  const [inView, setInView] = useState(false);

  /**
   * Which feature bodies are in the DOM below `lg`, as slide indexes.
   *
   * The accordion cannot simply mount all of them the way the desktop track
   * does: these illustrations are traced SVGs running ~300 CSS animations
   * between them, and six of those on a phone is what the old
   * one-card-under-the-list layout existed to avoid. So the open row's body goes
   * in at once and the row that just closed keeps its own for the panel's 380ms,
   * which is what gives the collapse something to animate against instead of
   * snapping shut. Never more than two at a time.
   */
  const [mounted, setMounted] = useState<number[]>([]);

  useEffect(() => {
    if (isDesktop) {
      setMounted([]);
      return;
    }
    if (active !== 0) {
      setMounted((prev) => (prev.includes(active) ? prev : [...prev, active]));
    }
    const timer = setTimeout(
      () => setMounted(active === 0 ? [] : [active]),
      PANEL_MS,
    );
    return () => clearTimeout(timer);
  }, [active, isDesktop]);

  /**
   * Below `lg` a feature opens inside the list, so a row far down would still
   * push its panel below the fold. Park the row just under the fixed 72px header
   * so the whole panel lands on screen — the same treatment the HackGrid product
   * page gives its accordion (see HackgridProduct.tsx `select`). Desktop keeps
   * the plain slide change.
   */
  const select = (i: number) => {
    const previous = active;
    go(i);
    if (isDesktop || i === 0) return;
    const row = rowRefs.current[i - 1];
    if (!row) return;
    // A tapped row is only pushed around by what closes ABOVE it, and that panel
    // takes 380ms to collapse — so measuring after the tap would aim at a
    // position the page is about to leave. Take the final position up front.
    const collapsingAbove =
      previous > 0 && previous < i
        ? (panelRefs.current[previous - 1]?.getBoundingClientRect().height ?? 0)
        : 0;
    const top = Math.max(
      0,
      row.getBoundingClientRect().top + window.scrollY - 88 - collapsingAbove,
    );
    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    requestAnimationFrame(() =>
      window.scrollTo({ top, behavior: smooth ? "smooth" : "auto" }),
    );
  };

  /**
   * The panel is well below the fold, so the video is only fetched and decoded
   * once it is actually on screen — `preload="metadata"` plus this is what
   * keeps 2.3MB off the initial load of a page most visitors enter at the hero.
   * threshold 0: the frame is 560px tall, and a threshold above 0 would simply
   * never fire on a short viewport (see the section list on this same page).
   */
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0 },
    );
    io.observe(frame);
    return () => io.disconnect();
  }, []);

  /**
   * Beyond that, the video only runs while it is the thing actually on screen —
   * no point decoding 13s of 720p behind a feature panel — and never under
   * `prefers-reduced-motion`, where the poster frame stands in for it. Playback
   * is started here rather than with the `autoplay` attribute so the
   * reduced-motion case never gets a frame of movement before being paused.
   *
   * "On screen" is not the same as `active === 0` at every width. From `lg` the
   * track slides the video out of the frame when a feature is picked, so it must
   * stop. Below `lg` the frame holds the video ALONE — features open in the
   * accordion beside it — so it stays in full view whatever row is open and
   * `active` says nothing about it. Gating on `active` there froze the video on
   * its poster in plain sight the moment a row was tapped.
   */
  const videoShowing = !isDesktop || active === 0;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (videoShowing && inView && !still) {
      void video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [videoShowing, inView]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,_550fr)_minmax(0,_675fr)] gap-6 lg:gap-12 items-stretch">
      {/* Feature list. Two skins on one piece of state, the same split the
          HackGrid product page uses (HackgridProduct.tsx):

          From `lg` it stays the Figma OT tab — a loose stack of gradient rows
          with a red left edge on the open one — driving the slider on the right.

          Below `lg` it is the site's shared mobile accordion instead (SHRED,
          Scarf, Local Regulatory, OT Security and HackGrid all wear it): one
          #171A1D card with hairline separators, 56px rows, and the open row
          marked by red bold text plus a flipped chevron. The feature opens
          directly under its own row rather than in a card beneath the whole
          list, which is what the list used to do — a tapped row could leave its
          content several rows further down, off screen.

          Only one of the two headers is ever rendered; the other is
          `display:none`, so it is out of the a11y tree and the tab order too. */}
      <div
        className={clsx(
          accordionCard,
          "flex flex-col",
          "lg:gap-2 lg:overflow-visible lg:rounded-none lg:bg-transparent",
        )}
      >
        {VISIBLE_FEATURES.map((feature, i) => {
          const slide = i + 1;
          const isActive = active === slide;
          const panelId = `ot-feature-${feature.id}`;
          // Clicking the open row again returns to the video, like the HackGrid
          // slider's "back to the first image".
          const toggle = () => select(isActive ? 0 : slide);
          return (
            <div
              key={feature.id}
              ref={(el) => {
                rowRefs.current[i] = el;
              }}
              className="flex flex-col"
            >
              {/* Accordion row — the sub-`lg` header. */}
              <button
                type="button"
                aria-expanded={isActive}
                aria-controls={panelId}
                onClick={toggle}
                className={clsx(
                  accordionHeader,
                  "lg:hidden",
                  i > 0 && accordionDivider,
                  // `accordionHeader` sets the resting weight (600); the open row
                  // goes to Bold so the selection reads at a glance. `!` because
                  // Tailwind emits font-weight utilities alphabetically, so
                  // `font-semibold` would otherwise outrank a plain `font-bold`.
                  isActive
                    ? "font-bold! text-primary"
                    : "text-text-default hover:text-text-highlight",
                )}
              >
                <span className="flex-1 min-w-0">{feature.title}</span>
                <AccordionChevron open={isActive} />
              </button>

              {/* Figma OT tab — the `lg`+ header. */}
              <button
                type="button"
                onClick={toggle}
                aria-pressed={isActive}
                className={clsx(
                  "group relative hidden lg:flex items-center rounded-[2px] px-4 sm:px-6 py-4 min-h-[64px] sm:min-h-[71px] text-left",
                  // Figma: Background gradient #2e353a -> rgba(21,24,29,0) (= #15181d at 0 alpha)
                  "bg-gradient-to-r from-[#2e353a] to-[#15181d00] transition-colors duration-300 cursor-pointer",
                  isActive
                    ? "border-l-[3px] border-primary"
                    : "hover:from-[#363d43]",
                )}
              >
                <span
                  className={clsx(
                    "text-[15px] sm:text-[16px] font-semibold leading-[24px] tracking-[0.5px] transition-colors duration-300",
                    isActive ? "text-primary" : "text-text-highlight",
                  )}
                >
                  {feature.title}
                </span>
              </button>

              {/* Sub-`lg` panel. The wrapper is always here so the height has
                  something to transition; the body itself comes and goes with
                  `mounted` (see above). */}
              <div
                id={panelId}
                ref={(el) => {
                  panelRefs.current[i] = el;
                }}
                role="region"
                aria-label={feature.title}
                inert={!isActive}
                className={clsx(
                  "lg:hidden",
                  accordionPanel,
                  isActive ? accordionPanelOpen : accordionPanelClosed,
                )}
                style={{ background: ACCORDION_PANEL_BG }}
              >
                <div className={accordionPanelClip}>
                  <div
                    className={clsx(
                      // Same panel padding as every other accordion on the site
                      // (`accordionBody`); the typography comes from FeatureBody,
                      // which the desktop slides share.
                      "px-4 pt-2 pb-6 sm:px-5 sm:pb-8",
                      "transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none",
                      isActive ? accordionBodyOpen : accordionBodyClosed,
                    )}
                  >
                    {mounted.includes(slide) ? (
                      <FeatureBody feature={feature} />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Right column. From `lg` it is one framed panel that slides between the
          default video and the six features, driven by the tab list — the same
          slider the HackGrid product page uses. Below `lg` there is no room to
          slide, so the panel holds the video alone and picking a feature swaps
          the card in beneath the list, exactly as this page behaved before. */}
      <div className="flex min-w-0 flex-col">
        {/* Figma: border #3f444a (Divider); bg linear-gradient #111113 73% ->
            #1d1e20 104% (Darker -> Surface 4). The frame is `lg:` only: below
            that the video sits on the page background like the artwork did. */}
        <div
          ref={frameRef}
          className={clsx(
            "relative w-full min-w-0 overflow-hidden flex flex-col",
            // 560px clears the tallest feature slide at every lg width (544px
            // at 1024, where the copy wraps most), so no slide has to scroll
            // and the frame never resizes between features.
            "lg:h-[560px] lg:rounded-[16px] lg:border lg:border-[#3f444a] lg:bg-gradient-to-b lg:from-[#111113] lg:from-[73%] lg:to-[#1d1e20] lg:to-[104%] lg:shadow-[-16px_21px_14px_rgba(0,0,0,0.3)]",
            // Below `lg` this used to be swapped out for the selected feature's
            // card. The accordion above owns that content now, so the video just
            // stays — the same way HackGrid keeps its wheel on a phone.
          )}
        >
          {/* Slide track. The offset only applies from `lg` (see the .ot-track
              rule) — below that the track holds the video alone. */}
          <div
            className="ot-track flex min-w-0 lg:h-full transition-transform duration-500 ease-out"
            style={{ "--ot-shift": `${active * 100}%` } as CSSProperties}
          >
            {/* Slide 0 — the default video, in place of the old control-cabinet
                illustration. 504 of the 675px right column in Figma. */}
            {/* The track is transformed, which makes it a stacking context, so
                the video's mix-blend-screen composites against THIS box, not
                against the frame behind it. Hence the backdrop is repeated
                here: the panel gradient from lg, the page colour below it —
                without a painted background the blend has nothing to work on
                and the clip shows up as a black rectangle. */}
            <div className="w-full shrink-0 flex items-center justify-center bg-background py-4 lg:h-full lg:bg-[linear-gradient(180deg,#111113_73%,#1d1e20_104%)] lg:p-6">
              <video
                ref={videoRef}
                src={VIDEO_SRC}
                poster={VIDEO_POSTER}
                muted
                loop
                playsInline
                // "none", not "metadata": Chrome answers a metadata preload
                // with `Range: bytes=0-` and pulls the whole 2.3MB before the
                // panel is ever on screen. The poster is what holds the frame
                // until the observer above starts playback.
                preload="none"
                aria-label="OT/ICS Simulation System control cabinet"
                // mix-blend-screen, not a plain box: the clip is white line art
                // on pure black, and screen leaves black as the backdrop — so
                // the cabinet sits on the panel gradient the way the traced
                // illustration it replaced did, instead of inside a black
                // rectangle. (Black is the identity for screen; the artwork's
                // own colours come through untouched.)
                className="w-full max-w-[504px] lg:max-w-none lg:max-h-full h-auto object-contain mix-blend-screen"
              />
            </div>

            {/* Slides 1..n — the features. Desktop only, and gated in JS rather
                than with `hidden`: these six illustrations are traced SVGs
                running ~300 CSS animations between them, so below `lg` — where
                the card under the list owns this content — they must not be in
                the DOM at all, not merely display:none. */}
            {isDesktop &&
              VISIBLE_FEATURES.map((feature, i) => (
                <div
                  key={feature.id}
                  data-active={active === i + 1}
                  className="ot-slide flex w-full shrink-0 h-full flex-col overflow-y-auto p-6"
                >
                  {/* my-auto (not justify-center) centres a short slide but
                      still lets a tall one scroll to both ends instead of
                      clipping. */}
                  <div className="my-auto">
                    <FeatureBody feature={feature} />
                  </div>
                </div>
              ))}
          </div>

          {/* Dots — the first one returns to the video. Hidden below `lg`,
              where the frame has only that one slide. */}
          <div className="absolute bottom-4 left-1/2 z-10 hidden -translate-x-1/2 lg:flex items-center gap-3">
            {Array.from({ length: SLIDE_COUNT }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => go(i)}
                aria-label={
                  i === 0
                    ? "Back to the overview video"
                    : `Show ${VISIBLE_FEATURES[i - 1].title}`
                }
                aria-current={active === i}
                className={clsx(
                  "h-2 rounded-full transition-all duration-300 cursor-pointer",
                  active === i
                    ? "w-6 bg-primary"
                    : "w-2 bg-neutral-500 hover:bg-text-highlight",
                )}
              />
            ))}
          </div>
        </div>

      </div>

      <style jsx>{`
        /* Only the video lives in the frame below lg, so the slide offset (fed
           in as --ot-shift) applies from lg up. */
        .ot-track {
          transform: none;
        }
        @media (min-width: 1024px) {
          .ot-track {
            transform: translateX(calc(-1 * var(--ot-shift, 0%)));
          }
        }
        .ot-slide {
          scrollbar-width: none;
        }
        .ot-slide::-webkit-scrollbar {
          display: none;
        }
        /* Every slide is mounted at once (that is what a track is), so without
           this all six illustrations would run their loops the whole time the
           section is on screen. They are pure CSS animations, so parking the
           off-screen ones costs nothing and they pick up where they left off.
           :global because the animations are declared inside the art
           components, not here. */
        .ot-slide[data-active="false"] :global(*) {
          animation-play-state: paused;
        }

        /* Attack-simulation radar: the two tick combs around SYSTEM turn
           continuously, counter to each other and at different periods so they
           never lock into step. */
        @keyframes otAttackDialSpin {
          to {
            transform: rotate(360deg);
          }
        }
        :global(.ot-attack-dial-inner),
        :global(.ot-attack-dial-outer) {
          transform-origin: 22.718% 48.768%;
        }
        :global(.ot-attack-dial-inner) {
          animation: otAttackDialSpin 48s linear infinite;
        }
        :global(.ot-attack-dial-outer) {
          animation: otAttackDialSpin 32s linear infinite reverse;
        }

        /* Phase 1 of 3 of the reveal: the flow lines draw outward from the wheel
           towards the chips. */
        @keyframes otAttackFlowDraw {
          from {
            clip-path: inset(0 100% 0 0);
          }
          to {
            clip-path: inset(0 49.594% 0 0);
          }
        }
        /* Phase 2: the technique chips land top to bottom. */
        @keyframes otAttackChipsDraw {
          from {
            clip-path: inset(10.04% 31.25% 89.96% 50.406%);
          }
          to {
            clip-path: inset(10.04% 31.25% 0 50.406%);
          }
        }
        /* Phase 3: the selected chip's sub-techniques fill in. */
        @keyframes otAttackPanelDraw {
          from {
            clip-path: inset(9.781% 0 90.219% 68.75%);
          }
          to {
            clip-path: inset(9.781% 0 36.551% 68.75%);
          }
        }
        :global(.ot-attack-flow) {
          animation: otAttackFlowDraw 1.6s ease-out 0.25s both;
        }
        :global(.ot-attack-chips) {
          animation: otAttackChipsDraw 1s linear 1.85s both;
        }
        :global(.ot-attack-panel) {
          animation: otAttackPanelDraw 0.85s linear 2.95s both;
        }

        /* The red dotted ring sweeps 45deg and back, pausing at each end. */
        @keyframes otAttackRingSweep {
          0%,
          8% {
            transform: rotate(0deg);
          }
          46%,
          54% {
            transform: rotate(45deg);
          }
          92%,
          100% {
            transform: rotate(0deg);
          }
        }
        :global(.ot-attack-ring) {
          transform-box: fill-box;
          transform-origin: center;
          animation: otAttackRingSweep 7s ease-in-out infinite;
        }
        /* Realistic Configuration Transfer — read (0.2s) -> cross (0.75s) ->
           process (1.45s) -> mirror builds (3.35s), then only the ambient
           loops keep running. Every draw-on uses pathLength=100 so one
           keyframe serves sweeps, gauges, spokes and rings alike. */
        @keyframes cfgDash {
          from {
            stroke-dashoffset: 100;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
        /* A packet crosses in the first 13% of the cycle, then waits out the
           rest — one animation gives both the story beat and its echo. */
        @keyframes cfgFlow {
          0% {
            stroke-dashoffset: 100;
            opacity: 0;
          }
          3% {
            opacity: 1;
          }
          13% {
            stroke-dashoffset: 0;
            opacity: 1;
          }
          17%,
          100% {
            stroke-dashoffset: 0;
            opacity: 0;
          }
        }
        @keyframes cfgReadBlink {
          0%,
          100% {
            opacity: 0;
          }
          2% {
            opacity: 1;
          }
          9% {
            opacity: 0;
          }
        }
        @keyframes cfgPop {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          60% {
            transform: scale(1.25);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        @keyframes cfgFade {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes cfgSpin {
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes cfgBreathe {
          0%,
          100% {
            opacity: 0.8;
          }
          50% {
            opacity: 1;
          }
        }

        :global(.cfg-scan-left) {
          animation: cfgDash 5.6s linear 0.15s infinite;
        }
        /* Everything that waits its turn starts invisible: during an
           animation-delay the element would otherwise render its base state,
           parking a stray packet or sweep on the artwork. */
        :global(.cfg-read-dot),
        :global(.cfg-flow-a),
        :global(.cfg-flow-b),
        :global(.cfg-scan-right) {
          opacity: 0;
        }
        :global(.cfg-read-dot) {
          animation: cfgReadBlink 5.6s ease-out infinite;
        }
        :global(.cfg-read-dot-0) {
          animation-delay: 0.2s;
        }
        :global(.cfg-read-dot-1) {
          animation-delay: 0.35s;
        }
        :global(.cfg-read-dot-2) {
          animation-delay: 0.5s;
        }
        :global(.cfg-flow-a) {
          animation: cfgFlow 6s linear 0.75s infinite;
        }
        :global(.cfg-flow-b) {
          animation: cfgFlow 6s linear 2.85s infinite;
        }
        :global(.cfg-gauge-mask) {
          animation: cfgDash 1s ease-out 1.45s both;
        }
        /* Full white while it is filling, then dimmed so the red arc keeps being
           the focal point rather than competing with a lit gauge. */
        @keyframes cfgGaugeSettle {
          from {
            opacity: 1;
          }
          to {
            opacity: 0.62;
          }
        }
        :global(.cfg-gauge-lit) {
          animation: cfgGaugeSettle 0.7s ease-in-out 2.5s both;
        }
        :global(.cfg-ring-sweep) {
          animation: cfgDash 7s linear infinite;
        }
        :global(.cfg-iris) {
          transform-origin: 795.5px 174.3px;
          animation: cfgSpin 9s linear 1.3s infinite;
        }
        :global(.cfg-red-arc) {
          animation: cfgDash 0.62s ease-out 2.4s both,
            cfgBreathe 3.6s ease-in-out 3.2s infinite;
        }
        :global(.cfg-node-core) {
          transform-origin: 1238.4px 308.2px;
          animation: cfgPop 0.42s cubic-bezier(0.34, 1.4, 0.64, 1) 3.35s both;
        }
        :global(.cfg-spoke) {
          animation: cfgDash 0.34s ease-out both;
        }
        :global(.cfg-spoke-0) {
          animation-delay: 3.5s;
        }
        :global(.cfg-spoke-1) {
          animation-delay: 3.55s;
        }
        :global(.cfg-spoke-2) {
          animation-delay: 3.6s;
        }
        :global(.cfg-spoke-3) {
          animation-delay: 3.65s;
        }
        :global(.cfg-pop) {
          animation: cfgPop 0.3s cubic-bezier(0.34, 1.4, 0.64, 1) both;
        }
        :global(.cfg-pop-inner-0) {
          animation-delay: 3.74s;
        }
        :global(.cfg-pop-inner-1) {
          animation-delay: 3.79s;
        }
        :global(.cfg-pop-inner-2) {
          animation-delay: 3.84s;
        }
        :global(.cfg-pop-inner-3) {
          animation-delay: 3.89s;
        }
        :global(.cfg-pop-outer-0) {
          animation-delay: 3.94s;
        }
        :global(.cfg-pop-outer-1) {
          animation-delay: 3.99s;
        }
        :global(.cfg-pop-outer-2) {
          animation-delay: 4.04s;
        }
        :global(.cfg-pop-outer-3) {
          animation-delay: 4.09s;
        }
        :global(.cfg-node-ring) {
          animation: cfgDash 0.75s ease-out 3.55s both;
        }
        :global(.cfg-node-tick) {
          animation: cfgFade 0.3s ease-out 4.15s both;
        }
        :global(.cfg-pop-corner-0) {
          animation-delay: 4.2s;
        }
        :global(.cfg-pop-corner-1) {
          animation-delay: 4.26s;
        }
        :global(.cfg-pop-corner-2) {
          animation-delay: 4.32s;
        }
        :global(.cfg-pop-corner-3) {
          animation-delay: 4.38s;
        }
        :global(.cfg-scan-right) {
          animation: cfgDash 5.6s linear infinite,
            cfgFade 0.5s linear 4.6s both;
        }

        /* The SCADA instrument icons: come online after the mirror exists, then
           their needles keep turning. */
        @keyframes cfgIconOnline {
          0% {
            opacity: 0;
            transform: scale(0.86);
          }
          70% {
            opacity: 1;
            transform: scale(1.04);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        /* One 6s cycle: a halo swells out of the icon, then the icon waits its
           turn again — staggering the delay walks the ping along the row. */
        @keyframes cfgPing {
          0% {
            transform: scale(1);
            opacity: 0;
          }
          4% {
            opacity: 0.5;
          }
          20%,
          100% {
            transform: scale(1.26);
            opacity: 0;
          }
        }
        :global(.cfg-icon) {
          opacity: 0;
          animation: cfgIconOnline 0.5s cubic-bezier(0.34, 1.35, 0.64, 1) both;
        }
        :global(.cfg-icon-0) {
          animation-delay: 4.05s;
        }
        :global(.cfg-icon-1) {
          animation-delay: 4.17s;
        }
        :global(.cfg-icon-2) {
          animation-delay: 4.29s;
        }
        :global(.cfg-icon-3) {
          animation-delay: 4.41s;
        }
        /* Each needle starts turning the moment its own icon lights up. At this
           size the icon is ~14px across on screen, so anything slower than a few
           seconds per turn reads as standing still. */
        /* A full turn, then the same turn unwound: 'alternate' plays the 0..360
           keyframe backwards on every second pass. */
        :global(.cfg-icon-slash) {
          animation: cfgSpin 4.5s linear infinite alternate;
        }
        :global(.cfg-icon-0 .cfg-icon-slash) {
          animation-delay: 4.05s;
        }
        /* Stagger the starts and vary the period so the row does not read as one
           geared machine. */
        :global(.cfg-icon-1 .cfg-icon-slash) {
          animation-delay: 4.17s;
          animation-duration: 5.4s;
        }
        :global(.cfg-icon-2 .cfg-icon-slash) {
          animation-delay: 4.29s;
        }
        :global(.cfg-icon-3 .cfg-icon-slash) {
          animation-delay: 4.41s;
          animation-duration: 5.4s;
        }
        /* The client half is already running when the card opens, so its
           instruments turn from the start rather than waiting for the mirror. */
        :global(.cfg-cube) {
          animation: cfgSpin 9s linear 0.3s infinite alternate;
        }
        :global(.cfg-target-rule) {
          animation: cfgSpin 6.5s linear 0.5s infinite alternate;
        }
        :global(.cfg-tri-slash) {
          animation: cfgSpin 5s linear 0.4s infinite alternate;
        }
        :global(.cfg-ping) {
          opacity: 0;
          animation: cfgPing 6s ease-out infinite;
        }
        :global(.cfg-ping-0) {
          animation-delay: 4.5s;
        }
        :global(.cfg-ping-1) {
          animation-delay: 4.62s;
        }
        :global(.cfg-ping-2) {
          animation-delay: 4.74s;
        }
        :global(.cfg-ping-3) {
          animation-delay: 4.86s;
        }
        :global(.cfg-ping-4) {
          animation-delay: 4.98s;
        }
        :global(.cfg-ping-5) {
          animation-delay: 5.1s;
        }
        :global(.cfg-ping-6) {
          animation-delay: 5.22s;
        }
        :global(.cfg-ping-7) {
          animation-delay: 5.34s;
        }

        @media (prefers-reduced-motion: reduce) {
          :global(.ot-attack-ring),
          :global(.ot-attack-dial-inner),
          :global(.ot-attack-dial-outer) {
            animation: none;
          }
          /* Keep the mirror whole, drop everything that only exists to move. */
          :global(.cfg-scan-left),
          :global(.cfg-scan-right),
          :global(.cfg-ring-sweep),
          :global(.cfg-read-dot),
          :global(.cfg-flow-a),
          :global(.cfg-flow-b) {
            animation: none;
            opacity: 0;
          }
          :global(.cfg-iris) {
            animation: none;
          }
          :global(.cfg-gauge-mask),
          :global(.cfg-red-arc),
          :global(.cfg-node-ring),
          :global(.cfg-spoke) {
            animation: cfgDash 1ms linear both;
          }
          :global(.cfg-node-core),
          :global(.cfg-pop) {
            animation: cfgPop 1ms linear both;
          }
          :global(.cfg-node-tick) {
            animation: cfgFade 1ms linear both;
          }
          :global(.cfg-gauge-lit) {
            animation: cfgGaugeSettle 1ms linear both;
          }
          :global(.cfg-icon) {
            animation: cfgIconOnline 1ms linear both;
          }
          :global(.cfg-icon-slash),
          :global(.cfg-cube),
          :global(.cfg-target-rule),
          :global(.cfg-tri-slash),
          :global(.cfg-ping) {
            animation: none;
          }
          :global(.cfg-ping) {
            opacity: 0;
          }
          /* Skip the reveal but keep the end state, so the artwork stays whole. */
          :global(.ot-attack-flow) {
            animation: otAttackFlowDraw 1ms linear both;
          }
          :global(.ot-attack-chips) {
            animation: otAttackChipsDraw 1ms linear both;
          }
          :global(.ot-attack-panel) {
            animation: otAttackPanelDraw 1ms linear both;
          }
        }
      `}</style>
    </div>
  );
};

export default OTKeyFeatures;
